import axios, { type AxiosInstance } from 'axios';
import { millisecondsToSeconds } from 'date-fns';
import { action, computed, flow, makeObservable, observable, runInAction } from 'mobx';

import { EventEmitter } from '../../../shared/EventEmitter';
import type { IDirectoriesService, TDictObject } from '../../../shared/IDirectoriesService';
import type { INotificationsService } from '../../../shared/INotificationsService';
import type { Wellbores } from '../../../shared/stores/WellboresStore';
import type { TUserData } from '../../../shared/types';
import type { SettingsStore } from '../../stores/SettingsStore';
import type { TManualAnomalyCreateDto, TManualAnomalyResponseDto, TManualAnomalyRowData } from '../types';

import { ManualAnomaliesApi } from './ManualAnomaliesApi';
import { ManualAnomaliesUiState } from './ManualAnomaliesUiState';
import { ManualAnomaliesValidationService } from './ManualAnomaliesValidationService';
import type { TData } from './ManualAnomaly.entity';
import { ManualAnomaly } from './ManualAnomaly.entity';

type TEventMap = {
  newManualAnomalies: ManualAnomaly[];
  deleteManualAnomalies: string[];
  updateManualAnomalies: ManualAnomaly[];
};

const mapManualAnomaly = (dto: TManualAnomalyResponseDto): TManualAnomalyRowData => {
  return {
    ...dto,
    indexFormat: dto.indexType,
    riskLevel: dto.riskLevel ?? 'NO_RISK',
    anomalyTypeId: dto.anomalyType?.typeId ?? undefined,
    anomalySubtypeId: dto.anomalyType?.subtypeId ?? undefined,
    unitId: dto.unitId ?? undefined,
    holeDepth: dto.holeDepth ?? null,
    bitDepth: dto.bitDepth ?? null,
    limitValue: dto.values?.limit ?? null,
    factValue: dto.values?.fact ?? null,
    deltaAbsolute: dto.values?.absoluteDiff ?? null,
    deltaRelative: dto.values?.relativeDiff ?? null,
    notification: dto.notification ?? '',
    recommendation: dto.recommendation ?? '',
    anomalyAt: dto.anomalyAt ? millisecondsToSeconds(new Date(dto.anomalyAt).getTime()) : undefined,
  };
};

const serializeManualAnomaly = (row: ManualAnomaly, wellId: number): TManualAnomalyCreateDto => {
  const anomaly = row.data;

  if (anomaly.indexFormat == null) {
    throw new Error('Index type is required');
  }
  if (anomaly.indexFormat === 'LOG_TIME' && anomaly.anomalyAt == null) {
    throw new Error('Event datetime is required');
  }
  if (anomaly.riskLevel == null) {
    throw new Error('Risk rang is required');
  }
  // if (anomaly.anomalyTypeId == null) {
  //   throw new Error('Anomaly type id is required');
  // }
  // if (anomaly.anomalySubtypeId == null) {
  //   throw new Error('Anomaly subtype id is required');
  // }
  // if (anomaly.unitId == null) {
  //   throw new Error('Unit id is required');
  // }

  return {
    id: anomaly.id ?? undefined,
    wellId,
    wellboreId: anomaly.wellboreId ?? undefined,
    indexType: anomaly.indexFormat,
    anomalyAt: anomaly.anomalyAt != null ? new Date(anomaly.anomalyAt * 1000).toISOString() : undefined,
    riskLevel: anomaly.riskLevel,
    anomalyType: {
      typeId: Number(anomaly.anomalyTypeId),
      subtypeId: Number(anomaly.anomalySubtypeId),
    },
    values: {
      limit: anomaly.limitValue ?? undefined,
      fact: anomaly.factValue ?? undefined,
    },
    // holeDepth: anomaly.holeDepth ?? undefined,
    bitDepth: anomaly.bitDepth ?? undefined,
    unitId: anomaly.unitId ?? undefined,
    notification: anomaly.notification ?? undefined,
    recommendation: anomaly.recommendation ?? undefined,
  };
};

export class ManualAnomaliesService {
  private readonly api: ManualAnomaliesApi;
  private readonly abortControllers = new Map<string, AbortController>();
  readonly validationService: ManualAnomaliesValidationService;

  readonly eventEmitter = new EventEmitter<TEventMap>();
  readonly uiState = new ManualAnomaliesUiState();

  @observable anomalyTypes: TDictObject[] = [];
  @observable anomalySubtypes: TDictObject[] = [];
  @observable workClassifierStageTypes: TDictObject[] = [];
  @observable wellboreTypes: TDictObject[] = [];
  @observable trajectoryTypes: TDictObject[] = [];
  @observable sectionTypes: TDictObject[] = [];
  @observable stratUnitTypes: TDictObject[] = [];
  @observable reservoirTypes: TDictObject[] = [];
  @observable runTypes: TDictObject[] = [];
  @observable unitsList: TDictObject[] = [];
  @observable operationTypes: TDictObject[] = [];

  @observable loading = false;
  @observable areDirectoriesLoaded = false;

  @observable private manualAnomalies: ManualAnomaly[] = [];
  @observable deletedManualAnomaliesIds: string[] = [];

  manualAnomaliesMap = observable.map<string, ManualAnomaly>();

  constructor(
    private readonly wellId: number,
    agent: AxiosInstance,
    private readonly notifications: INotificationsService,
    private readonly userData: TUserData,
    public readonly settingsStore: SettingsStore,
    public readonly wellbores: Wellbores,
    private readonly directoryService: IDirectoriesService,
  ) {
    this.validationService = new ManualAnomaliesValidationService();
    this.api = new ManualAnomaliesApi(agent);
    makeObservable(this);
  }

  private serializeManualAnomalies = (anomalies: ManualAnomaly[]): TManualAnomalyCreateDto[] => {
    return anomalies.map((anomaly) => serializeManualAnomaly(anomaly, this.wellId));
  };

  @flow.bound
  private *fetchAnomalies() {
    this.loading = true;
    this.abortControllers.get('fetchManualAnomalies')?.abort();
    const ac = new AbortController();
    this.abortControllers.set('fetchManualAnomalies', ac);

    try {
      const list: TManualAnomalyResponseDto[] = yield this.api.fetchList(this.wellId, ac.signal);
      this.manualAnomalies = list.map((d) => new ManualAnomaly(mapManualAnomaly(d), this.userData));
      this.manualAnomaliesMap.clear();
      this.manualAnomaliesMap.merge(this.manualAnomalies.map((anomaly) => [anomaly.entityId, anomaly]));
    } catch (e) {
      if (axios.isCancel(e)) return;
      this.notifications.showErrorMessageT('alertsManagerWidget:errorMessages.fetchManualAnomalies');
      console.error(e);
    } finally {
      this.loading = false;
      this.abortControllers.delete('fetchManualAnomalies');
    }
  }

  @computed
  get rows(): ManualAnomaly[] {
    return this.manualAnomalies;
  }

  @computed
  get hasUnsavedChanges(): boolean {
    return (
      this.manualAnomalies.some((row) => row.isChanged || row.id == null) || this.deletedManualAnomaliesIds.length > 0
    );
  }

  private getDefaultManualAnomalyData(): TData {
    return { riskLevel: 'HIGH_RISK' };
  }

  private getCurrentAnomalyAt(): number {
    return millisecondsToSeconds(Date.now());
  }

  private getAnomalyTypeIdBySubtype(subtypeId: TData['anomalySubtypeId']): number | undefined {
    const anomalyTypeId = this.anomalySubtypes.find((subtype) => subtype.id === subtypeId)?.data['anomalyType'];

    return typeof anomalyTypeId === 'number' ? anomalyTypeId : undefined;
  }

  private normalizeManualAnomalyPatch(baseData: TData, patch: Partial<TData>): Partial<TData> {
    const normalizedPatch: Partial<TData> = { ...patch };

    if ('indexFormat' in patch && patch.indexFormat !== 'LOG_DEPTH' && !('wellboreId' in patch)) {
      normalizedPatch.wellboreId = undefined;
    }

    if ('indexFormat' in patch && patch.indexFormat !== 'LOG_DEPTH' && !('bitDepth' in patch)) {
      normalizedPatch.bitDepth = undefined;
    }

    if ('indexFormat' in patch && patch.indexFormat !== 'LOG_TIME' && !('anomalyAt' in patch)) {
      normalizedPatch.anomalyAt = undefined;
    }

    if (
      'indexFormat' in patch &&
      patch.indexFormat === 'LOG_TIME' &&
      baseData.anomalyAt == null &&
      !('anomalyAt' in patch)
    ) {
      normalizedPatch.anomalyAt = this.getCurrentAnomalyAt();
    }

    if ('anomalyTypeId' in patch && !('anomalySubtypeId' in patch)) {
      normalizedPatch.anomalySubtypeId = undefined;
    }

    if ('anomalySubtypeId' in patch && patch.anomalySubtypeId != null && !('anomalyTypeId' in patch)) {
      const anomalyTypeId = this.getAnomalyTypeIdBySubtype(patch.anomalySubtypeId);

      if (anomalyTypeId != null) {
        normalizedPatch.anomalyTypeId = anomalyTypeId;
      }
    }

    return normalizedPatch;
  }

  private createManualAnomalyDataFromPatch(patch: Partial<TData>): TData {
    const baseData = this.getDefaultManualAnomalyData();
    const normalizedPatch = this.normalizeManualAnomalyPatch(baseData, patch);

    return {
      ...baseData,
      ...normalizedPatch,
    };
  }

  addNewManualAnomalyItems(initialRecordsData?: TData[]): void {
    if (!initialRecordsData) {
      const newItem = new ManualAnomaly(this.getDefaultManualAnomalyData(), this.userData);
      this.manualAnomalies.push(newItem);
      this.manualAnomaliesMap.set(newItem.entityId, newItem);
      this.eventEmitter.emit('newManualAnomalies', [newItem]);

      return;
    }

    if (!initialRecordsData.length) {
      return;
    }

    const newManualAnomalies: ManualAnomaly[] = [];

    for (const initialData of initialRecordsData) {
      const newItem = new ManualAnomaly(initialData, this.userData);

      this.manualAnomalies.push(newItem);
      newManualAnomalies.push(newItem);
      this.manualAnomaliesMap.set(newItem.entityId, newItem);
    }

    this.eventEmitter.emit('newManualAnomalies', newManualAnomalies);
  }

  @action.bound
  addNewManualAnomalyItemPatches(patches: Partial<TData>[]): void {
    this.addNewManualAnomalyItems(patches.map((patch) => this.createManualAnomalyDataFromPatch(patch)));
  }

  updateManualAnomalyItems(data: [string, TData][]): void {
    const updatedManualAnomalies: ManualAnomaly[] = [];

    for (const [recordId, anomalyData] of data) {
      const seqItem = this.manualAnomaliesMap.get(recordId);

      if (!seqItem) {
        console.warn(`cannot find manual anomaly item with id ${recordId}`);
        continue;
      }

      seqItem.updateData(anomalyData);
      updatedManualAnomalies.push(seqItem);
    }

    this.eventEmitter.emit('updateManualAnomalies', updatedManualAnomalies);
  }

  @action.bound
  applyManualAnomalyItemPatches(data: [string, Partial<TData>][]): void {
    const updatedManualAnomalies: ManualAnomaly[] = [];

    for (const [recordId, patch] of data) {
      const anomalyItem = this.manualAnomaliesMap.get(recordId);

      if (!anomalyItem) {
        console.warn(`cannot find manual anomaly item with id ${recordId}`);
        continue;
      }

      const normalizedPatch = this.normalizeManualAnomalyPatch(anomalyItem.data, patch);
      anomalyItem.updateData({ ...anomalyItem.data, ...normalizedPatch });
      updatedManualAnomalies.push(anomalyItem);
    }

    this.eventEmitter.emit('updateManualAnomalies', updatedManualAnomalies);
    this.validationService.validateAnomalies(updatedManualAnomalies);
  }

  @action.bound
  deleteManualAnomalyItems(ids: string[]) {
    const firstSeqItemToDeleteIndex = this.manualAnomalies.findIndex(({ entityId }) => ids.includes(entityId));

    if (firstSeqItemToDeleteIndex < 0) {
      return;
    }

    const idsMap = new Map(ids.map((id) => [id, id]));

    for (let i = 0; i < this.manualAnomalies.length; ) {
      const anomaly = this.manualAnomalies[i];

      if (idsMap.has(anomaly.entityId)) {
        this.manualAnomaliesMap.delete(anomaly.entityId);
        this.manualAnomalies.splice(i, 1);

        if (anomaly.id != null) {
          this.deletedManualAnomaliesIds.push(anomaly.id);
        }

        continue;
      }

      i++;
    }

    this.eventEmitter.emit('deleteManualAnomalies', ids);
  }

  onManualAnomalyItemValueChange<K extends keyof TManualAnomalyRowData>(
    recordId: string,
    field: K,
    value: TManualAnomalyRowData[K],
  ): void {
    const anomalyItem = this.manualAnomaliesMap.get(recordId);

    if (!anomalyItem) {
      console.error(`cannot find corresponding item with id ${recordId}`);
      return;
    }

    const patch = { [field]: value } as Partial<TData>;
    this.applyManualAnomalyItemPatches([[recordId, patch]]);
  }

  setEditing(value: boolean) {
    this.uiState.setEditing(value);
  }

  nameFromDictList(list: TDictObject[], id: number | string, nameKeys: string[]): string {
    if (isEmptyContextId(id)) {
      return '-';
    }
    const row = list.find((o) => String(o.id) === String(id));
    if (!row) {
      return String(id);
    }
    for (const k of nameKeys) {
      const v = row.data[k];
      if (v != null && String(v).trim() !== '') {
        return String(v).trim();
      }
    }
    return String(id);
  }

  @flow.bound
  private async *fetchDirectories() {
    try {
      const [
        anomalyTypes,
        anomalySubtypes,
        workClassifierStageTypes,
        wellboreTypes,
        trajectoryTypes,
        sectionTypes,
        stratUnitTypes,
        reservoirTypes,
        runTypes,
        unitsList,
        operationTypes,
      ] = await this.directoryService.loadObjects([
        'Common_AnomalyTypes',
        'Common_AnomalySubTypes',
        'GOreport_WorkClassifier_Stage',
        'Common_WellboreType',
        'Common_TrajectoryType',
        'Common_SectionType',
        'Common_StratUnitType',
        'Common_Reservoir',
        'Common_RunType',
        'Common_Uom',
        'Common_AutosupervisorOperations',
      ]);
      yield;

      this.anomalyTypes = anomalyTypes ?? [];
      this.anomalySubtypes = anomalySubtypes ?? [];
      this.workClassifierStageTypes = workClassifierStageTypes ?? [];
      this.wellboreTypes = wellboreTypes ?? [];
      this.trajectoryTypes = trajectoryTypes ?? [];
      this.sectionTypes = sectionTypes ?? [];
      this.stratUnitTypes = stratUnitTypes ?? [];
      this.reservoirTypes = reservoirTypes ?? [];
      this.runTypes = runTypes ?? [];
      this.unitsList = unitsList ?? [];
      this.operationTypes = operationTypes ?? [];

      this.areDirectoriesLoaded = true;
    } catch (e) {
      yield;
      console.error(e);
    }
  }

  @action.bound
  saveManualAnomalies() {
    let manualAnomalies: TManualAnomalyCreateDto[] = [];
    if (this.validationService.validateAnomalies(this.manualAnomalies)) {
      return Promise.reject(new Error('Validation errors'));
    }
    try {
      manualAnomalies = this.serializeManualAnomalies(
        this.manualAnomalies.filter((anomaly) => anomaly.isChanged || anomaly.id == null),
      );
    } catch (e) {
      console.error(e);
      return Promise.reject(new Error('Serialization errors'));
    }

    const createManualAnomalies: TManualAnomalyCreateDto[] = [];
    const updateManualAnomalies: TManualAnomalyCreateDto[] = [];
    const deleteManualAnomalies: string[] = this.deletedManualAnomaliesIds;

    for (const anomaly of manualAnomalies) {
      if (anomaly.id == null) {
        createManualAnomalies.push(anomaly);
      } else {
        updateManualAnomalies.push(anomaly);
      }
    }
    return this.api
      .save({
        create: createManualAnomalies,
        update: updateManualAnomalies,
        delete: deleteManualAnomalies,
      })
      .then((r) => {
        runInAction(() => {
          this.deletedManualAnomaliesIds = [];
        });
        return r;
      })
      .then(() => {
        return this.fetchAnomalies();
      })
      .catch((e) => {
        this.notifications.showErrorMessageT('alertsManagerWidget:errorMessages.saveManualAnomalies');
        console.error(e);
        throw e;
      });
  }

  @flow.bound
  *discardChanges() {
    this.deletedManualAnomaliesIds = [];

    yield this.fetchAnomalies();
  }

  loadInitialData() {
    return Promise.all([this.fetchAnomalies(), this.wellbores.fetchWellbores(this.wellId), this.fetchDirectories()]);
  }

  init = (): VoidFunction => {
    void this.loadInitialData();
    return () => {
      this.abortControllers.forEach((c) => c.abort());
      this.wellbores.clear();
    };
  };
}

function isEmptyContextId(id: number | string): boolean {
  return id === 0 || id === '' || (typeof id === 'string' && id.trim() === '');
}
